import React, { useMemo } from 'react'
import { FormScreen, FormValidationSchema, FormValues } from 'components/screen-templates/FormScreen'
import { onBackClickCallback, onNextClickType } from 'components/types'
import { Quizzes } from 'redux/quiz/types'
import { GifName } from '../../data/gifs'
import { totalLbsFromStoneAndLbs } from 'libs/weight/weightCalculations'
import * as Yup from 'yup'
import { Answer } from 'clients/quiz-service'
import { IntlShape, useIntl } from 'react-intl'
import { DSFlex, DSInput, DSSpacer } from '@zoe/ds-web'
import { useFormContext, useController } from 'react-hook-form'
import { ZiggieAsk } from '../core/ziggie/ZiggieAsk'

export type InputTargetWeightScreenProps = {
  title: string
  descriptionLbs: string
  descriptionKg: string
  topImage?: string
  topImageAlt?: string
  gif?: GifName
  buttonText?: string
  // Ziggie asks the question from a speech bubble, in place of the title and description.
  withZiggie?: { gif: GifName; typewriter?: boolean }
}

type Props = InputTargetWeightScreenProps & {
  onBackClick?: onBackClickCallback
  onNextClick: onNextClickType
  quiz: Quizzes
}

export const InputTargetWeightScreen: React.FC<Props> = ({
  quiz,
  title,
  descriptionLbs,
  descriptionKg,
  topImage,
  topImageAlt,
  gif,
  buttonText,
  withZiggie,
  onNextClick,
  onBackClick,
}) => {
  const intl = useIntl()
  // Default to whatever unit the user picked on the previous (current weight) screen.
  const defaultWeightUnit = quiz.weight_unit || (quiz.weight_lbs ? 'lbs' : quiz.weight_kg ? 'kg' : 'kg')
  const selectedUnit = defaultWeightUnit
  const initialValues = useMemo(
    () => ({
      ideal_weight_lbs: '',
      ideal_weight_kg: '',
      ideal_weight_st: '',
      ideal_weight_st_lbs: '',
      weight_unit: defaultWeightUnit,
    }),
    [defaultWeightUnit],
  )

  const handleOnNextClick = (arg1?: FormValues | Answer[] | any) => {
    const args = arg1 || {}
    if (args.weight_unit === 'stone') {
      onNextClick({ ideal_weight_lbs: totalLbsFromStoneAndLbs(args.ideal_weight_st, args.ideal_weight_st_lbs || 0) })
    } else if (args.weight_unit === 'lbs') {
      onNextClick({ ideal_weight_lbs: args.ideal_weight_lbs })
    } else {
      onNextClick({ ideal_weight_kg: args.ideal_weight_kg })
    }
  }

  return (
    <FormScreen
      topImage={topImage}
      topImageAlt={topImageAlt}
      gif={gif}
      screenTitle={title}
      buttonText={buttonText}
      description={selectedUnit === 'kg' ? descriptionKg : descriptionLbs}
      questionSlot={
        withZiggie && <ZiggieAsk gif={withZiggie.gif} text={title} quiz={quiz} typewriter={withZiggie.typewriter} />
      }
      form={{
        body: <TargetWeightScreenBody selectedUnit={selectedUnit} />,
        validationSchema: targetWeightValidationSchema(intl),
        // needed to prevent React "uncontrolled" error showing
        initialValues,
      }}
      onNextClick={handleOnNextClick}
      onBackClick={onBackClick}
    />
  )
}

type TargetWeightScreenBodyProps = {
  selectedUnit: string
}

const TargetWeightScreenBody = ({ selectedUnit }: TargetWeightScreenBodyProps) => {
  const {
    formState: { errors, touchedFields },
  } = useFormContext<any>()

  const idealWeightLbs = useController({ name: 'ideal_weight_lbs' })
  const idealWeightKg = useController({ name: 'ideal_weight_kg' })
  const idealWeightSt = useController({ name: 'ideal_weight_st' })
  const idealWeightStLbs = useController({ name: 'ideal_weight_st_lbs' })

  return (
    <div>
      {selectedUnit === 'lbs' && (
        <DSInput
          id="ideal_weight_lbs"
          name={idealWeightLbs.field.name}
          value={idealWeightLbs.field.value ?? ''}
          onChange={idealWeightLbs.field.onChange}
          onBlur={idealWeightLbs.field.onBlur}
          placeholder=""
          suffix="lbs"
          type="number"
          testId="ideal-weight-lbs-input"
          isInvalid={!!touchedFields.ideal_weight_lbs && !!errors.ideal_weight_lbs}
          errorMessage={touchedFields.ideal_weight_lbs ? (errors.ideal_weight_lbs?.message as string) : undefined}
        />
      )}

      {selectedUnit === 'kg' && (
        <DSInput
          id="ideal_weight_kg"
          name={idealWeightKg.field.name}
          value={idealWeightKg.field.value ?? ''}
          onChange={idealWeightKg.field.onChange}
          onBlur={idealWeightKg.field.onBlur}
          placeholder=""
          suffix="kg"
          type="number"
          testId="ideal-weight-kg-input"
          isInvalid={!!touchedFields.ideal_weight_kg && !!errors.ideal_weight_kg}
          errorMessage={touchedFields.ideal_weight_kg ? (errors.ideal_weight_kg?.message as string) : undefined}
        />
      )}

      {selectedUnit === 'stone' && (
        <DSFlex direction="row">
          <DSInput
            id="ideal_weight_st"
            name={idealWeightSt.field.name}
            value={idealWeightSt.field.value ?? ''}
            onChange={idealWeightSt.field.onChange}
            onBlur={idealWeightSt.field.onBlur}
            placeholder=""
            suffix="st"
            type="number"
            testId="ideal-weight-st-input"
            isInvalid={!!touchedFields['ideal_weight_st'] && !!errors['ideal_weight_st']}
            errorMessage={touchedFields['ideal_weight_st'] ? (errors['ideal_weight_st']?.message as string) : undefined}
          />
          <DSSpacer size={12} direction="horizontal" />
          <DSInput
            id="ideal_weight_st_lbs"
            name={idealWeightStLbs.field.name}
            value={idealWeightStLbs.field.value ?? ''}
            onChange={idealWeightStLbs.field.onChange}
            onBlur={idealWeightStLbs.field.onBlur}
            placeholder=""
            suffix="lbs"
            type="number"
            testId="ideal-weight-st-lbs-input"
            isInvalid={!!touchedFields['ideal_weight_st_lbs'] && !!errors['ideal_weight_st_lbs']}
            errorMessage={
              touchedFields['ideal_weight_st_lbs'] ? (errors['ideal_weight_st_lbs']?.message as string) : undefined
            }
          />
        </DSFlex>
      )}
    </div>
  )
}

const targetWeightValidationSchema = (intl: IntlShape): FormValidationSchema =>
  Yup.object({
    weight_unit: Yup.string(),
    ideal_weight_lbs: Yup.number().when('weight_unit', {
      is: (weight_unit: string) => weight_unit === 'lbs',
      then: () => Yup.number().required(intl.formatMessage({ id: 'quiz.ideal_weight.weight_field.error' })),
      otherwise: () => Yup.mixed().notRequired(),
    }),
    ideal_weight_kg: Yup.number().when('weight_unit', {
      is: (weight_unit: string) => weight_unit === 'kg',
      then: () => Yup.number().required(intl.formatMessage({ id: 'quiz.ideal_weight.weight_field.error' })),
      otherwise: () => Yup.mixed().notRequired(),
    }),
    ideal_weight_st: Yup.number().when('weight_unit', {
      is: (weight_unit: string) => weight_unit === 'stone',
      then: () => Yup.number().required(intl.formatMessage({ id: 'quiz.ideal_weight.weight_field.error' })),
      otherwise: () => Yup.mixed().notRequired(),
    }),
    ideal_weight_st_lbs: Yup.number().when('weight_unit', {
      is: (weight_unit: string) => weight_unit === 'stone',
      then: () =>
        Yup.number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .notRequired()
          .min(0, 'You cannot enter a negative number')
          .max(13, intl.formatMessage({ id: 'quiz.weight_validation.lbs_in_stone' })),
      otherwise: () => Yup.mixed().notRequired(),
    }),
  })
